import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { COURSES_LIST } from "@/lib/data";
import { PRICING, tierForCourseCount, usePricing } from "@/lib/pricing";

const STORAGE_KEY = "sp-cart-v3";
const CartCtx = createContext(null);

const ALL_SLUGS = COURSES_LIST.map((c) => c.slug);

// Smart cart pricing — matches backend compute_cart_pricing()
function deriveCart(state) {
  const courses = (state.courses || []).filter((s) => ALL_SLUGS.includes(s));
  const n = courses.length;
  const tier = tierForCourseCount(n);
  const coursePrice = PRICING.coursePrice;

  let original = n * coursePrice;
  let price = original;
  let savings = 0;
  let packageId = null;
  let packageName = null;

  if (tier) {
    packageId = tier.id;
    packageName = tier.name;
    price = tier.price;
    original = tier.strike || tier.courseCount * coursePrice;
    savings = Math.max(original - price, 0);
  }

  // Next-tier nudge — the cheapest tier that unlocks more courses than we have
  const upgrade = [PRICING.tiers.elite, PRICING.tiers.basic, PRICING.tiers.premium].find(
    (t) => t && t.active !== false && t.courseCount > n,
  );
  let nextTier = null;
  if (upgrade) {
    const addCount = upgrade.courseCount - n;
    nextTier = {
      label: `Add ${addCount} More Course${addCount > 1 ? "s" : ""} & Unlock ${
        upgrade.id === "premium" ? "Complete Transformation" : upgrade.name
      }`,
      addCount,
      targetPackage: upgrade.id,
      // What they'd save vs buying that many courses individually
      saveAmount: Math.max((upgrade.strike || upgrade.courseCount * coursePrice) - upgrade.price, 0),
      // What they'd save vs the amount their current cart costs today
      saveFromCurrent: Math.max(price - upgrade.price, 0),
    };
  }

  return {
    courses: courses.slice(0, PRICING.maxCartCourses),
    n,
    original,
    price,
    savings,
    packageId,
    packageName,
    nextTier,
    isEmpty: n === 0,
  };
}

export function CartProvider({ children }) {
  const [state, setState] = useState({ courses: [] });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.courses)) setState(parsed);
      }
    } catch {
      // ignore corrupted storage
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (hydrated) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state, hydrated]);

  const addCourse = useCallback((slug) => {
    setState((s) => {
      if (!ALL_SLUGS.includes(slug)) return s;
      if (s.courses.includes(slug)) return s;
      if (s.courses.length >= PRICING.maxCartCourses) return s;
      return { ...s, courses: [...s.courses, slug] };
    });
  }, []);

  const removeCourse = useCallback((slug) => {
    setState((s) => ({ ...s, courses: s.courses.filter((x) => x !== slug) }));
  }, []);

  const toggleCourse = useCallback((slug) => {
    setState((s) => {
      if (!ALL_SLUGS.includes(slug)) return s;
      if (s.courses.includes(slug)) {
        return { ...s, courses: s.courses.filter((x) => x !== slug) };
      }
      if (s.courses.length >= PRICING.maxCartCourses) return s;
      return { ...s, courses: [...s.courses, slug] };
    });
  }, []);

  const addAllCourses = useCallback(() => {
    setState({ courses: ALL_SLUGS.slice() });
  }, []);

  const clear = useCallback(() => setState({ courses: [] }), []);

  const pricing = usePricing();
  const cart = useMemo(() => deriveCart(state), [state, pricing.coursePrice, pricing.tiers]);

  const value = useMemo(
    () => ({
      cart,
      addCourse,
      removeCourse,
      toggleCourse,
      addAllCourses,
      clear,
      has: (slug) => cart.courses.includes(slug),
    }),
    [cart, addCourse, removeCourse, toggleCourse, addAllCourses, clear],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}

export function formatInr(n) {
  return `₹${(n || 0).toLocaleString("en-IN")}`;
}
